import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { FreightClass } from "~/shared/schemas/freight-classes-schema.ts";
import type {
	QuoteBody,
	QuoteCommodity,
} from "~/shared/schemas/quote-schema.ts";
import type { UnitType } from "~/shared/schemas/unit-type-schema.ts";
import type {
	HandlingUnit,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";
import { idGenerator } from "~/shared/utils/id-generator.ts";

import type { LocationValue } from "./quote-location-utils.ts";
import {
	deriveCountry,
	mapStopDetailsToAddressBookItem,
	updatePostalCode,
} from "./quote-location-utils.ts";

type QuoteBodyHydrationResult = {
	nextState: QuoteStateType;
	pickupItem: AddressBookItemSummary | null;
	deliveryItem: AddressBookItemSummary | null;
};

type MapQuoteBodyOptions = {
	accessorials?: Accessorial[];
	unitTypes?: UnitType[];
	freightClasses?: FreightClass[];
};

const coerceNumberOrNull = (value: unknown) => {
	if (value == null) {
		return null;
	}
	if (typeof value === "number") {
		return Number.isNaN(value) ? null : value;
	}
	if (typeof value === "string") {
		const parsed = Number(value);
		return Number.isNaN(parsed) ? null : parsed;
	}
	return null;
};

const coerceStringOrNull = (value: unknown) =>
	typeof value === "string" ? value : null;

const coerceNumberFromString = (value?: string | null) => {
	if (!value) return 0;
	const parsed = Number(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const mapQuoteCommodityToHandlingUnit = (
	commodity: QuoteCommodity,
	options: MapQuoteBodyOptions,
): HandlingUnit => {
	const unitType = options.unitTypes?.find(
		(type) => type.id === commodity.unitTypeId,
	);
	const freightClass = options.freightClasses?.find(
		(item) => item.id === commodity.freightClassId,
	);

	return {
		id: commodity.id ?? idGenerator(),
		name: commodity.description ?? "",
		description: commodity.description ?? "",
		quantity: commodity.quantity,
		unit: {
			id: commodity.unitTypeId,
			name: unitType?.name ?? String(commodity.unitTypeId),
			code: unitType?.code ?? String(commodity.unitTypeId),
		},
		weight: commodity.weight || null,
		density: 0,
		dimensionLength: commodity.dimensionLength,
		dimensionWidth: commodity.dimensionWidth,
		dimensionHeight: commodity.dimensionHeight,
		class: {
			id: commodity.freightClassId,
			name:
				freightClass?.name ??
				(commodity.freightClassId ? String(commodity.freightClassId) : ""),
			code:
				freightClass?.code ??
				freightClass?.name ??
				(commodity.freightClassId ? String(commodity.freightClassId) : ""),
		},
		nmfc: commodity.nmfc ?? "",
		isHazmat: commodity.isHazmat,
		isStackable: commodity.isStackable,
		pieceCount: commodity.pieceCaseCount,
		width: commodity.dimensionWidth,
		height: commodity.dimensionHeight,
		length: commodity.dimensionLength,
		estClass: 0,
		properShippingName: "",
		unNumber: null,
		hazmatClass: undefined,
		packingGroupId: undefined,
		emergencyContactName: "",
		emergencyContactPhone: "",
	};
};

const mapAccessorials = (
	accessorials: QuoteBody["accessorials"] | undefined,
	options: MapQuoteBodyOptions,
) => {
	if (!accessorials?.length || !options.accessorials?.length) {
		return [] as Accessorial[];
	}
	const ids = new Set(accessorials.map((acc) => acc.id));
	return options.accessorials.filter((acc) => ids.has(acc.id));
};

export const mapQuoteBodyToQuoteState = (
	body: Partial<QuoteBody> & { calculatedLinearFeet?: number | null },
	prevState: QuoteStateType,
	options: MapQuoteBodyOptions = {},
): QuoteBodyHydrationResult => {
	const origin = body.origin;
	const destination = body.destination;

	const pickupItem = mapStopDetailsToAddressBookItem(
		body.pickupDetails ?? undefined,
		"pickup",
		origin as LocationValue | undefined,
	);
	const deliveryItem = mapStopDetailsToAddressBookItem(
		body.deliveryDetails ?? undefined,
		"delivery",
		destination as LocationValue | undefined,
	);

	const items = (body.quoteCommodities ?? []).map((commodity) =>
		mapQuoteCommodityToHandlingUnit(commodity, options),
	);
	const resolvedAccessorials = mapAccessorials(body.accessorials, options);
	const insuranceValue = coerceNumberOrNull(body.insuranceValue as unknown);
	const insuranceProductType = coerceStringOrNull(
		body.insuranceProductType as unknown,
	);
	const templateTotalLinearFeet = coerceNumberFromString(body.totalLinearFeet);
	const templateCalculatedLinearFeet = coerceNumberOrNull(
		body.calculatedLinearFeet,
	);
	const hasCalculatedLinearFeet = templateCalculatedLinearFeet != null;
	const resolvedTotalLinearFeet = hasCalculatedLinearFeet
		? templateCalculatedLinearFeet
		: templateTotalLinearFeet;
	let resolvedCustomTotalLinearFeet = 0;

	if (
		hasCalculatedLinearFeet &&
		templateCalculatedLinearFeet !== templateTotalLinearFeet
	) {
		resolvedCustomTotalLinearFeet = templateTotalLinearFeet;
	}

	return {
		nextState: {
			...prevState,
			shipmentDate: body.shipmentDate ?? prevState.shipmentDate,
			publicGuid: body.publicGuid ?? prevState.publicGuid,
			templateId: body.templateId ?? prevState.templateId,
			templateName: prevState.templateName,
			customer: {
				...prevState.customer,
				id: body.customer?.id ?? prevState.customer.id,
				name: body.customer?.name ?? prevState.customer.name,
			},
			customerDetails: {
				...prevState.customerDetails,
				id: body.customer?.id ?? prevState.customerDetails.id,
				name: body.customer?.name ?? prevState.customerDetails.name,
			},
			pickupCountry: deriveCountry(prevState.pickupCountry, origin?.country),
			deliveryCountry: deriveCountry(
				prevState.deliveryCountry,
				destination?.country,
			),
			pickupPostalCode: updatePostalCode(prevState.pickupPostalCode, origin),
			deliveryPostalCode: updatePostalCode(
				prevState.deliveryPostalCode,
				destination,
			),
			items: items.length > 0 ? items : prevState.items,
			pieceCaseCount: body.pieceCaseCount ?? prevState.pieceCaseCount,
			totalLinearFeet: resolvedTotalLinearFeet,
			customTotalLinearFeet: resolvedCustomTotalLinearFeet,
			customerReference: body.customerReferences ?? prevState.customerReference,
			poNumbers: body.poNumbers ?? prevState.poNumbers,
			soNumber: body.soNumbers ?? prevState.soNumber,
			specialInstructions:
				body.specialInstructions ?? prevState.specialInstructions,
			insuranceValue: insuranceValue ?? prevState.insuranceValue,
			insuranceApplyType:
				body.insuranceApplyTypeId ?? prevState.insuranceApplyType,
			insuranceApplyTypeId:
				body.insuranceApplyTypeId ?? prevState.insuranceApplyTypeId,
			insuranceProductType:
				insuranceProductType ?? prevState.insuranceProductType,
			insuranceProductTypeId:
				body.insuranceProductTypeId ?? prevState.insuranceProductTypeId,
			selectedCarrierPriceId:
				body.selectedCarrierPriceId ?? prevState.selectedCarrierPriceId,
			isGuest: body.isGuest ?? prevState.isGuest,
			puNumber: body.pickupDetails?.puNumber ?? prevState.puNumber,
			deliveryPO: body.deliveryDetails?.deliveryPO ?? prevState.deliveryPO,
			pickupServices: resolvedAccessorials.filter(
				(acc) => acc.stopType?.name === "Pick",
			),
			deliveryServices: resolvedAccessorials.filter(
				(acc) => acc.stopType?.name === "Drop",
			),
			addtDetails: resolvedAccessorials.filter((acc) =>
				["BOND", "BLIND_D", "BLIND_S", "PFZ"].includes(acc.code ?? ""),
			),
		},
		pickupItem,
		deliveryItem,
	};
};
